import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export const Lightbox = ({ images, index, onClose, onIndex }) => {
  const open = index !== null && index >= 0 && images.length > 0;
  const touch = useRef(0);
  const go = (d) => onIndex((index + d + images.length) % images.length);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % images.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [open, index, images.length, onClose, onIndex]);

  const img = open ? images[index] : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Visualização ampliada"
          data-testid="lightbox"
          className="fixed inset-0 z-[60] bg-[#08080A]/95 backdrop-blur-xl flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - touch.current;
            if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
          }}
        >
          <div className="flex items-center justify-between px-5 sm:px-8 h-16 text-white/70 text-xs tracking-[0.25em]">
            <span data-testid="lightbox-counter">{String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
            <button onClick={onClose} aria-label="Fechar" data-testid="lightbox-close-button" className="h-11 w-11 -mr-2 flex items-center justify-center hover:text-white">
              <X className="w-6 h-6" strokeWidth={1.2} />
            </button>
          </div>
          <div className="relative flex-1 flex items-center justify-center px-4 sm:px-20 min-h-0">
            <AnimatePresence mode="wait">
              <motion.img
                key={img.url + index}
                src={mediaUrl(img.url)}
                alt={img.caption || "Foto do Gran Hotel Sé"}
                className="max-h-full max-w-full object-contain select-none"
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                data-testid="lightbox-image"
              />
            </AnimatePresence>
            {images.length > 1 && (
              <>
                <button onClick={() => go(-1)} aria-label="Foto anterior" data-testid="lightbox-prev-button" className="absolute left-2 sm:left-6 h-12 w-12 flex items-center justify-center border border-white/15 bg-[#08080A]/60 text-white hover:border-white/50 transition-colors">
                  <ChevronLeft className="w-5 h-5" strokeWidth={1.2} />
                </button>
                <button onClick={() => go(1)} aria-label="Próxima foto" data-testid="lightbox-next-button" className="absolute right-2 sm:right-6 h-12 w-12 flex items-center justify-center border border-white/15 bg-[#08080A]/60 text-white hover:border-white/50 transition-colors">
                  <ChevronRight className="w-5 h-5" strokeWidth={1.2} />
                </button>
              </>
            )}
          </div>
          <p className="min-h-16 px-6 py-5 text-center font-display italic text-lg text-[#D4D4D8]" data-testid="lightbox-caption">
            {img.caption}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
